import React, {useCallback, useEffect, useState} from 'react';
import {View, FlatList, StyleSheet, RefreshControl, TouchableOpacity, Image} from 'react-native';
import {Text, Chip, FAB, ActivityIndicator, Appbar} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import {useOrderStore} from '../../store/useOrderStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import OrgUnitSelector from '../../components/common/OrgUnitSelector';
import {colors, spacing} from '../../theme';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return {day: String(d.getDate()), month: MONTHS[d.getMonth()]};
};

const FILTER_OPTIONS = [
  {key: 'ALL', statuses: null},
  {key: 'NEW', statuses: ['NEW']},
  {key: 'IN_PROGRESS', statuses: ['IN_PROCESS']},
  {key: 'READY', statuses: ['COMPLETED', 'QUALITY_CHECK', 'READY_FOR_TRIAL']},
  {key: 'DELIVERED', statuses: ['DELIVERED']},
];

const StatusChip = ({status, t}) => (
  <Chip
    style={[styles.statusChip, {backgroundColor: (colors.status[status] || colors.info) + '20'}]}
    textStyle={styles.statusChipText}>
    <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{t(`order.status.${status}`)}</Text>
  </Chip>
);

const OrderListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {orders, isLoading, pagination, fetchOrders, setFilters, clearFilters} = useOrderStore();
  const {selectedOrgUnitId} = useOrgUnitStore();
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const option = FILTER_OPTIONS.find(f => f.key === activeFilter);
    fetchOrders({page: 0, size: 20, ...(option.statuses ? {statuses: option.statuses} : {}), orgUnitId: selectedOrgUnitId});
  }, [fetchOrders, selectedOrgUnitId]);

  const handleFilter = useCallback(filterKey => {
    setActiveFilter(filterKey);
    const option = FILTER_OPTIONS.find(f => f.key === filterKey);
    if (option.statuses) {
      setFilters({statuses: option.statuses});
      fetchOrders({page: 0, size: 20, statuses: option.statuses, orgUnitId: selectedOrgUnitId});
    } else {
      clearFilters();
      fetchOrders({page: 0, size: 20, orgUnitId: selectedOrgUnitId});
    }
  }, [setFilters, clearFilters, fetchOrders, selectedOrgUnitId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const option = FILTER_OPTIONS.find(f => f.key === activeFilter);
      await fetchOrders({page: 0, size: 20, ...(option.statuses ? {statuses: option.statuses} : {}), orgUnitId: selectedOrgUnitId});
    } finally {
      setRefreshing(false);
    }
  }, [activeFilter, fetchOrders, selectedOrgUnitId]);

  const handleLoadMore = useCallback(() => {
    if (isLoading) return;
    const nextPage = pagination.page + 1;
    if (nextPage < pagination.totalPages) {
      fetchOrders({page: nextPage, size: pagination.size});
    }
  }, [isLoading, pagination, fetchOrders]);

  const getAmountColor = (paymentStatus) => {
    if (paymentStatus === 'PAID') return colors.success;
    if (paymentStatus === 'UNPAID') return colors.error;
    if (paymentStatus === 'PARTIALLY_PAID') return colors.warning;
    return colors.text;
  };

  const renderOrder = useCallback(({item}) => {
    const date = parseDate(item.deliveryDate);
    const amountColor = getAmountColor(item.paymentStatus);
    const summary = item.itemSummary || [];
    const totalItems = summary.reduce((sum, s) => sum + s.quantity, 0);
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.row}
        onPress={() => navigation.navigate('OrderView', {orderId: item.id})}>
        <View style={styles.dateCol}>
          {date ? (
            <>
              <Text style={styles.dateDay}>{date.day}</Text>
              <Text style={styles.dateMonth}>{date.month}</Text>
            </>
          ) : (
            <Text style={styles.datePlaceholder}>—</Text>
          )}
        </View>
        <View style={styles.detailsCol}>
          <Text style={styles.orderNumber}>{item.orderNumber}</Text>
          <Text style={styles.customerName} numberOfLines={1}>{item.customerName}</Text>
          {summary.length > 0 && (
            <View style={styles.summaryRow}>
              <View style={styles.summaryIcons}>
                {summary.map((s, idx) => (
                  <View key={idx} style={styles.summaryItem}>
                    {s.catalogItemIcon ? (
                      <Image source={{uri: s.catalogItemIcon}} style={styles.summaryIcon} />
                    ) : (
                      <View style={styles.summaryIconFallback}>
                        <Text style={styles.summaryIconLetter}>{(s.catalogItemName || '?').charAt(0)}</Text>
                      </View>
                    )}
                    {s.quantity > 1 && <Text style={styles.summaryQty}>x{s.quantity}</Text>}
                  </View>
                ))}
              </View>
              <Text style={styles.summaryMeta}>
                {totalItems} item{totalItems > 1 ? 's' : ''}
                {summary.filter(s => s.serviceName).map(s => s.serviceName).filter((v, i, a) => a.indexOf(v) === i).map(name => ` \u00B7 ${name}`).join('')}
              </Text>
            </View>
          )}
          <View style={styles.statusRow}>
            <StatusChip status={item.status} t={t} />
            {item.urgent && <Icon name="flag" size={18} color={colors.error} style={{marginLeft: 6}} />}
          </View>
        </View>
        <View style={styles.amountCol}>
          {item.paymentStatus === 'PARTIALLY_PAID' && (
            <Text style={styles.totalAmountStrike}>
              {'\u20B9'}{Number((item.totalAmount || 0) - (item.discountAmount || 0) + (item.taxAmount || 0)).toLocaleString()}
            </Text>
          )}
          <Text style={[styles.orderAmount, {color: amountColor}]}>
            {'\u20B9'}{Number(
              item.paymentStatus === 'PAID'
                ? (item.totalAmount || 0) - (item.discountAmount || 0) + (item.taxAmount || 0)
                : (item.balanceAmount || 0)
            ).toLocaleString()}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }, [navigation, t]);

  const filterLabels = {ALL: 'All', NEW: 'New', IN_PROGRESS: 'In Progress', READY: 'Ready', DELIVERED: 'Delivered'};

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action icon="menu" onPress={() => navigation.dispatch(DrawerActions.openDrawer())} />
        <Appbar.Content title={t('order.title')} />
        <Appbar.Action icon="filter-variant" onPress={() => navigation.navigate('OrderSearchFilters')} />
        <OrgUnitSelector />
      </Appbar.Header>

      <View style={styles.filterRow}>
        {FILTER_OPTIONS.map(opt => (
          <Chip
            key={opt.key}
            selected={activeFilter === opt.key}
            showSelectedCheck={activeFilter === opt.key}
            selectedColor={activeFilter === opt.key ? colors.white : undefined}
            onPress={() => handleFilter(opt.key)}
            style={[styles.filterChip, activeFilter === opt.key && styles.filterChipActive]}
            textStyle={activeFilter === opt.key ? styles.filterChipTextActive : styles.filterChipText}>
            <Text style={{fontSize: 13, color: activeFilter === opt.key ? '#fff' : '#000', marginTop: 3}}>{filterLabels[opt.key]}</Text>
          </Chip>
        ))}
      </View>

      {isLoading && orders.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={orders}
          renderItem={renderOrder}
          keyExtractor={item => String(item.id)}
          contentContainerStyle={styles.listContent}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.3}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text variant="titleMedium" style={styles.emptyTitle}>{t('common.noData')}</Text>
            </View>
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
          }
        />
      )}

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={() => navigation.navigate('OrderCreate')}
        color={colors.white}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  filterRow: {
    flexDirection: 'row', paddingHorizontal: spacing.sm, paddingVertical: spacing.sm,
    backgroundColor: colors.surface, gap: spacing.xs,
  },
  filterChip: {backgroundColor: colors.background},
  filterChipActive: {backgroundColor: colors.primary},
  filterChipText: {color: colors.text, fontSize: 12},
  filterChipTextActive: {color: colors.white, fontSize: 12},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {paddingBottom: 80},
  row: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.sm + 3, paddingHorizontal: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: 1, borderBottomColor: colors.divider,
  },
  dateCol: {
    width: '20%', alignItems: 'center', justifyContent: 'center',
  },
  dateDay: {fontSize: 24, fontWeight: '400', color: colors.text, lineHeight: 28},
  dateMonth: {fontSize: 11, color: colors.textSecondary, fontWeight: '600', letterSpacing: 1},
  datePlaceholder: {fontSize: 24, color: colors.textLight},
  detailsCol: {flex: 1, paddingHorizontal: spacing.sm},
  orderNumber: {fontWeight: 'bold', color: colors.primary, fontSize: 14},
  customerName: {color: colors.text, fontSize: 13, marginVertical: 2},
  summaryRow: {flexDirection: 'row', alignItems: 'center', marginVertical: 3},
  summaryIcons: {flexDirection: 'row', alignItems: 'center', gap: 4},
  summaryItem: {flexDirection: 'row', alignItems: 'center'},
  summaryIcon: {width: 20, height: 20, borderRadius: 4, backgroundColor: colors.border},
  summaryIconFallback: {
    width: 20, height: 20, borderRadius: 4, backgroundColor: '#EDE7F6',
    alignItems: 'center', justifyContent: 'center',
  },
  summaryIconLetter: {fontSize: 10, fontWeight: '700', color: colors.primary},
  summaryQty: {fontSize: 10, color: colors.textSecondary, marginLeft: 1, fontWeight: '600'},
  summaryMeta: {fontSize: 11, color: colors.textSecondary, marginLeft: 6},
  statusRow: {flexDirection: 'row', alignItems: 'center', marginTop: 2},
  statusChip: {paddingVertical: 0, height: 24},
  statusChipText: {fontSize: 14, color: colors.text},
  amountCol: {alignItems: 'flex-end', minWidth: 80},
  totalAmountStrike: {fontSize: 12, color: colors.textSecondary, textDecorationLine: 'line-through', marginBottom: 1},
  orderAmount: {fontSize: 14, fontWeight: '700'},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: spacing.xxl * 2},
  emptyTitle: {color: colors.textSecondary},
  fab: {position: 'absolute', right: spacing.md, bottom: spacing.md, backgroundColor: colors.primary},
});

export default OrderListScreen;
